import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Drawer,
    IconButton,
    Grid,
    Button,
    Divider,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { isEqual, cloneDeep } from 'lodash';
import {
    RESET_AWARDING_RULE,
    createAwardingRule,
    editAwardingRule,
} from '../../store/awardingRule';
import { AdminSidePanelAction } from '../../../Enums';
import InformationSection from './InformationSection';
import ParameterSection from './ParametersSection';
import './AwardingRuleEdit.scss';

/**
 * @param {{ open: boolean; setOpen: Function; }} props
 */
function AwardingRuleEdit(props) {
    const dispatch = useDispatch();
    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminAwardingRule, adminAppData } = useSelector((state) => state);
    const { adminSidePanelAction, isUsed } = adminAwardingRule;
    const { awardingRules } = adminAppData;
    const { open, setOpen } = props;

    const disabledSections = adminSidePanelAction === AdminSidePanelAction.Edit && isUsed
        ? {
            parameters: true,
        }
        : {
            parameters: false,
        };

    const [validationErrors, setValidationErrors] = useState({
        name: false,
        dataviewId: false,
        parameters: false,
    });

    useEffect(() => {
        setValidationErrors({
            name: false,
            dataviewId: false,
            parameters: false,
        });
    }, [JSON.stringify(adminAwardingRule)]);

    const validate = () => {
        let hasError = false;
        const errors = {
            name: false,
            dataviewId: false,
            parameters: false,
        };
        const {
            ruleSetId: id,
            name,
            dataviewId,
            parameters,
        } = adminAwardingRule;

        // Information section
        if (!name.trim()) {
            hasError = true;
            errors.name = true;
        } else {
            const value = awardingRules.find((x) => x.name === name && x.ruleSetId !== id);
            if (value) {
                hasError = true;
                errors.name = true;
            }
        }

        // Parameters section
        if (dataviewId === -1) {
            hasError = true;
            errors.dataviewId = true;
        }
        if (!parameters.every((x) => x.dataviewKeyAlias)) {
            hasError = true;
            errors.parameters = true;
        }

        if (errors.name !== validationErrors.name || errors.dataviewId !== validationErrors.dataviewId
            || errors.parameters !== validationErrors.parameters) {
            setValidationErrors(errors);
        }

        return !hasError;
    };

    const closeSidePanel = () => {
        dispatch({ type: RESET_AWARDING_RULE });
        setOpen(false);
    };

    const handleSubmitSetup = () => {
        if (validate()) {
            switch (adminSidePanelAction) {
                case AdminSidePanelAction.Create:
                    dispatch(createAwardingRule({ ...adminAwardingRule, ruleSetId: -1 }, () => setOpen(false), false));
                    break;
                case AdminSidePanelAction.Duplicate:
                    dispatch(createAwardingRule({ ...adminAwardingRule, ruleSetId: -1 }, () => setOpen(false), true));
                    break;
                case AdminSidePanelAction.Edit:
                    dispatch(editAwardingRule(adminAwardingRule, { awardingRules }, () => setOpen(false)));
                    break;
                default:
                    break;
            }
        }
    };

    const [originalAwardingRule, setOriginalAwardingRule] = useState(cloneDeep(adminAwardingRule));
    useEffect(() => setOriginalAwardingRule(cloneDeep(adminAwardingRule)), [adminAwardingRule.ruleSetId]);
    const isDirty = isEqual(originalAwardingRule, adminAwardingRule)
        && adminAwardingRule.adminSidePanelAction !== AdminSidePanelAction.Duplicate;

    return (
        <Drawer
            anchor="right"
            open={open}
            classes={{ paper: 'setupEditDrawerPaper' }}
        >
            <Grid
                container
                justifyContent="space-between"
                className="sue-header-section"
            >
                <Grid
                    item
                    className="title-container"
                >
                    <h3 className="title-label">
                        Rule Set
                    </h3>
                </Grid>
                <Grid item>
                    <IconButton
                        className="drawer-x-icon-button"
                        onClick={() => closeSidePanel()}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            size="sm"
                        />
                    </IconButton>
                </Grid>
            </Grid>
            <Grid
                container
                className="sue-content-section"
            >
                <InformationSection validationErrors={validationErrors} />
                <ParameterSection
                    validationErrors={validationErrors}
                    isDisabled={disabledSections.parameters}
                />
            </Grid>
            <div>
                <Divider />
                <Grid
                    container
                    justifyContent="flex-end"
                    className="awarding-ruleset-save--actions"
                >
                    <Button
                        variant="contained"
                        onClick={handleSubmitSetup}
                        disabled={isDirty}
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        onClick={closeSidePanel}
                    >
                        Cancel
                    </Button>
                </Grid>
            </div>
        </Drawer>
    );
}

export default AwardingRuleEdit;
